import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import type { FleetCapacityByType } from "./utils";
import type { FleetJobRow, SlotBlock } from "./types";
import {
  loadSlotColours,
  loadVehicleCapacity,
  loadVehicleTypeCompatibility,
  type VehicleTypeRow,
  type SlotColourRow,
  type VehicleCapacityRow,
  type CompatibilityRow,
} from "@/lib/vehicleTypes";
import { buildSlotSheet, placeUnassignedJobs, placeUnassignedCrossFleetJobs, placeScheduledBreaks, prepareLaneForDisplay, type ScheduledBreakForPlacement } from "./slotSheetUtils";
import { getSlotColour, type SlotColourFamily } from "./slotColours";
import { formatTime12, fetchAllUnassignedForwardJobs, nzHourFromIso, timeToHours, trimToHHMM, STS_TYPE_KEY, TTC_TYPE_KEY } from "./utils";
import ColumnFilterHeader from "./ColumnFilterHeader";
import { supabase } from "@/lib/supabase";
import { fetchConfirmedMultiLegGroups, loadMultiLegSettings, type ConfirmedMultiLegGroup } from "@/lib/multiLegGroups";
import { formatPassengerName } from "@/lib/passengerName";

const BLANK = "(Blanks)";

interface FleetSlotSheetProps {
  dataByType: Record<string, FleetCapacityByType>;
  vehicleTypes: VehicleTypeRow[];
  dateStr: string;
  /**
   * False skips the cross-type "not yet assigned" placement pass (see
   * placeUnassignedJobs). Needed for the STS/TTC tabs: each renders with
   * exactly one pseudo-type, and placeUnassignedJobs' "no specific type
   * required" fallback tries every type in the current render -- with only
   * STS (or only TTC) present, every unplaced booking system-wide (not just
   * that company's own) would land there. Their own unassigned bookings get
   * a dedicated placement pass instead -- see placeUnassignedCrossFleetJobs
   * below.
   */
  enableUnassignedPlacement?: boolean;
}

interface FlatEntry {
  slotLabel: string;
  colourFamily: SlotColourFamily;
  isOverflow: boolean;
  block: SlotBlock;
  vehicleKey: string;
  timeKey: string;
  stageKey: string;
  whenKey: string;
  assignKey: string;
  pobKey: string;
  reqKey: string;
  passengerKey: string;
  pickupKey: string;
  destinationKey: string;
  durationKey: string;
  slotBadge: string;
}

interface ColumnFilters {
  slot: Set<string> | null;
  vehicle: Set<string> | null;
  time: Set<string> | null;
  stage: Set<string> | null;
  when: Set<string> | null;
  assign: Set<string> | null;
  pob: Set<string> | null;
  req: Set<string> | null;
  passenger: Set<string> | null;
  pickup: Set<string> | null;
  destination: Set<string> | null;
  duration: Set<string> | null;
}

export interface FleetSlotSheetHandle {
  jumpToNow: () => void;
}

const formatMinutes = (decimalHours: number): string => {
  const totalMinutes = Math.round(decimalHours * 60);
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

const equipmentBadges = (v: VehicleCapacityRow | undefined): string => {
  if (!v) return "";
  const parts: string[] = [];
  if (v.wc_onboard) parts.push("Hoist");
  if (v.has_carseat) parts.push("CS");
  if (v.has_booster) parts.push("BST");
  if (v.has_paywave) parts.push("PW");
  if (v.is_tgb_vehicle) parts.push("TGB");
  if (v.has_500kg_hoist) parts.push("500kg");
  return parts.join(" ");
};

/** Hover tooltip for the Vehicle cell -- capacity numbers + the same equipment flags the old Equip column showed. */
const vehicleTooltip = (v: VehicleCapacityRow | undefined): string => {
  if (!v) return "";
  const parts: string[] = [];
  if (v.seat_capacity != null) parts.push(`Seats: ${v.seat_capacity}`);
  if (v.wheelchair_capacity != null) parts.push(`Wheelchair: ${v.wheelchair_capacity}`);
  if (v.luggage_capacity != null) parts.push(`Luggage: ${v.luggage_capacity}`);
  const equip = equipmentBadges(v);
  if (equip) parts.push(equip);
  return parts.join(" · ");
};

// Short badge for what the booking itself asked for (Sedan/SUV/Wheelchair
// Van/unset) -- distinct from the Seats/W/C/Luggage/Equip columns, which
// describe what the ASSIGNED vehicle actually has. requires_wheelchair_vehicle
// is kept as a fallback for older rows written before requested_vehicle_type
// existed (see migration 0034).
const REQUESTED_TYPE_ABBR: Record<string, string> = {
  Sedan: "Sedan",
  "SUV/Van": "SUV",
  "Wheelchair Van": "TMV",
  "Not specified": "",
};

const requestedTypeBadge = (job: { requested_vehicle_type: string | null; requires_wheelchair_vehicle: boolean | null } | null): string => {
  if (!job) return "";
  if (job.requested_vehicle_type) return REQUESTED_TYPE_ABBR[job.requested_vehicle_type] ?? job.requested_vehicle_type;
  if (job.requires_wheelchair_vehicle) return "TMV";
  return "";
};

// Icon + count for each of the booking's own tags -- omits anything zero or
// unknown so a plain Sedan job with no luggage doesn't show a row of zeros.
const jobCountBadges = (job: { passenger_count: number | null; wheelchair_count: number | null; luggage_count: number | null } | null): string => {
  if (!job) return "";
  const parts: string[] = [];
  if (job.passenger_count) parts.push(`\u{1F464}${job.passenger_count}`); // 👤
  if (job.wheelchair_count) parts.push(`\u{267F}${job.wheelchair_count}`); // ♿
  if (job.luggage_count) parts.push(`\u{1F9F3}${job.luggage_count}`); // 🧳
  return parts.join(" ");
};

const formatClockTime = (startH: number): string => {
  const h = Math.floor(startH) % 24;
  const m = Math.round((startH - Math.floor(startH)) * 60);
  return formatTime12(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
};

/** requested_pickup_at/assigned_at are UTC ISO timestamps -- render as NZ-local clock time. */
const formatIsoTime = (iso: string | null): string => {
  const h = nzHourFromIso(iso);
  return h === null ? "—" : formatClockTime(h);
};

/** pu_time is already an NZ-local "HH:MM:SS" wall-clock string, not an ISO timestamp. */
const formatPobTime = (job: { pu_date: string | null; pu_time: string | null } | null): string => {
  if (!job?.pu_time) return "—";
  return formatTime12(job.pu_time.slice(0, 5));
};

// Matches TaxiCaller's own colour language for the booking lifecycle stage
// (not the vehicle-type/lane colouring the rest of the row already carries).
const STAGE_STYLES: Record<string, { bg: string; text: string }> = {
  "Not started": { bg: "#374151", text: "#D1D5DB" },
  Waiting: { bg: "#CA8A04", text: "#FEF9C3" },
  Callout: { bg: "#2563EB", text: "#DBEAFE" },
  Arrived: { bg: "#EA580C", text: "#FFEDD5" },
  "Passenger on board": { bg: "#DC2626", text: "#FEE2E2" },
  Delivered: { bg: "#059669", text: "#D1FAE5" },
  Cancelled: { bg: "#7F1D1D", text: "#FCA5A5" },
  "No show": { bg: "#7F1D1D", text: "#FCA5A5" },
};

const stageBadge = (stage: string | null | undefined): React.ReactNode => {
  if (!stage) return null;
  const style = STAGE_STYLES[stage] ?? { bg: "#374151", text: "#D1D5DB" };
  return (
    <span
      className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap"
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      {stage}
    </span>
  );
};

/**
 * "Now" only replaces the label for the single open slot that's actually
 * happening right now (its own start/end window contains the current
 * moment) -- every other slot, past or future, always shows its real tick
 * time so the fixed-interval spacing (7:30, 7:45, 8:00...) stays visible.
 */
const formatSlotTime = (block: SlotBlock, dateStr: string): string => {
  if (block.kind === "job") return formatClockTime(block.startH);
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  if (dateStr === todayStr) {
    const nowH = now.getHours() + now.getMinutes() / 60;
    if (block.startH <= nowH && block.endH > nowH) return "Now";
  }
  return formatClockTime(block.startH);
};

const FleetSlotSheet = forwardRef<FleetSlotSheetHandle, FleetSlotSheetProps>(({ dataByType, vehicleTypes, dateStr, enableUnassignedPlacement = true }, ref) => {
  const [slotColours, setSlotColours] = useState<SlotColourRow[]>([]);
  const [vehicleCapacity, setVehicleCapacity] = useState<VehicleCapacityRow[]>([]);
  const [compatMatrix, setCompatMatrix] = useState<CompatibilityRow[]>([]);
  const [unassignedJobs, setUnassignedJobs] = useState<FleetJobRow[]>([]);
  const [publishedBreaks, setPublishedBreaks] = useState<ScheduledBreakForPlacement[]>([]);
  const [confirmedMultiLegGroups, setConfirmedMultiLegGroups] = useState<ConfirmedMultiLegGroup[]>([]);
  const [multiLegBufferMinutes, setMultiLegBufferMinutes] = useState(5);

  useEffect(() => {
    loadSlotColours().then(setSlotColours);
  }, [vehicleTypes]);

  useEffect(() => {
    loadVehicleCapacity().then(setVehicleCapacity);
  }, []);

  useEffect(() => {
    loadVehicleTypeCompatibility().then(setCompatMatrix);
  }, []);

  // Not-yet-assigned prebookings (see placeUnassignedJobs) -- fetched once
  // across every type, not per type, since seating one may need to check a
  // compatible type's open slots too. Volume is small; polled on the same
  // cadence as the main job/shift fetch (parent's useFleetCapacityDataForAllTypes)
  // so newly-created or newly-assigned bookings show up without a reload.
  useEffect(() => {
    if (!enableUnassignedPlacement) return;
    let cancelled = false;
    const load = () => {
      fetchAllUnassignedForwardJobs().then((jobs) => {
        if (!cancelled) setUnassignedJobs(jobs);
      });
    };
    load();
    const id = setInterval(load, 25_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [dateStr, enableUnassignedPlacement]);

  useEffect(() => {
    loadMultiLegSettings().then((s) => setMultiLegBufferMinutes(s.bufferMinutes));
  }, []);

  // Confirmed multi-leg groups (see placeUnassignedJobs) -- polled the same
  // way as unassignedJobs so ticking "Multi-leg" on the review tab shows up
  // here within the same cadence, without needing a page reload.
  useEffect(() => {
    if (!enableUnassignedPlacement) return;
    let cancelled = false;
    const load = () => {
      fetchConfirmedMultiLegGroups().then((groups) => {
        if (!cancelled) setConfirmedMultiLegGroups(groups);
      });
    };
    load();
    const id = setInterval(load, 25_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enableUnassignedPlacement]);

  // Published scheduled breaks for this day -- overlaid onto the relevant
  // vehicle's gap slots (see placeScheduledBreaks) so a driver's break shows
  // as a yellow block right in their vehicle's own row.
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      supabase
        .from("roster_scheduled_breaks")
        .select("driver_name, vehicle_number, suggested_start_hhmm, suggested_end_hhmm, confirmed_start_hhmm, confirmed_end_hhmm, taken")
        .eq("break_date", dateStr)
        .eq("status", "published")
        .then(({ data }) => {
          if (cancelled || !data) return;
          const mapped: ScheduledBreakForPlacement[] = data
            .filter((row) => row.vehicle_number)
            .map((row) => {
              const startHHMM = row.confirmed_start_hhmm || row.suggested_start_hhmm;
              const endHHMM = row.confirmed_end_hhmm || row.suggested_end_hhmm;
              return {
                vehicleNumber: row.vehicle_number as string,
                driverName: row.driver_name,
                startH: timeToHours(trimToHHMM(startHHMM)),
                endH: timeToHours(trimToHHMM(endHHMM)),
                taken: !!row.taken,
              };
            });
          setPublishedBreaks(mapped);
        });
    };
    load();
    const id = setInterval(load, 25_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [dateStr]);

  // Live "now" marker -- ticks every 30s so the highlighted row group and the
  // "Now" label (see formatSlotTime) both advance to the next slot on their
  // own without a page refresh. Browsers throttle setInterval heavily on a
  // backgrounded/inactive tab (dispatch screens routinely sit open for
  // hours), so the interval alone can leave "Now" hours stale -- also force
  // an immediate tick on visibilitychange so it catches up the moment the
  // tab is looked at again, rather than waiting on a throttled timer.
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 30_000);
    const onVisible = () => {
      if (document.visibilityState === "visible") setNowTick((n) => n + 1);
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);
  const isTodayView = useMemo(() => {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    return dateStr === todayStr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStr, nowTick]);
  const nowH = useMemo(() => {
    const now = new Date();
    return now.getHours() + now.getMinutes() / 60;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowTick]);

  // Auto-scroll to the current-time row group once per date shown (on
  // mount, and again whenever the viewed date changes back to today, e.g.
  // clicking "Today") -- not on every 30s tick, so it doesn't yank the
  // dispatcher's scroll position away if they're reading something else.
  const nowRowRef = React.useRef<HTMLTableRowElement | null>(null);
  const hasAutoScrolledRef = React.useRef(false);
  useEffect(() => {
    hasAutoScrolledRef.current = false;
  }, [dateStr]);
  useEffect(() => {
    if (!hasAutoScrolledRef.current && nowRowRef.current) {
      nowRowRef.current.scrollIntoView({ block: "center" });
      hasAutoScrolledRef.current = true;
    }
  });
  const jumpToNow = () => {
    nowRowRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  };
  useImperativeHandle(ref, () => ({ jumpToNow }));

  const capacityByVehicle = useMemo(() => {
    const map = new Map<string, VehicleCapacityRow>();
    vehicleCapacity.forEach((v) => map.set(v.vehicle_number, v));
    return map;
  }, [vehicleCapacity]);

  const baseSheets = useMemo(
    () =>
      vehicleTypes.map((t) => ({
        type: t,
        sheet: buildSlotSheet(dataByType[t.vehicle_type]?.shifts || [], dataByType[t.vehicle_type]?.jobs || [], dateStr, t),
      })),
    [vehicleTypes, dataByType, dateStr]
  );

  // Seats not-yet-assigned prebookings into a genuinely open gap slot (own
  // type first, then whatever's configured as compatible in Settings) before
  // falling back to that type's overflow lane -- see placeUnassignedJobs.
  const isCrossFleetOnly =
    vehicleTypes.length === 1 && (vehicleTypes[0].vehicle_type === STS_TYPE_KEY || vehicleTypes[0].vehicle_type === TTC_TYPE_KEY);
  const perTypeSheets = useMemo(() => {
    const afterGeneric = enableUnassignedPlacement
      ? placeUnassignedJobs(baseSheets, unassignedJobs, compatMatrix, dateStr, confirmedMultiLegGroups, multiLegBufferMinutes)
      : baseSheets;
    if (!isCrossFleetOnly) return afterGeneric;
    // This tab's own unassigned bookings (no vehicle yet -- see
    // fetchCrossFleetCapacity) get their own gap-seeking pass, since they
    // don't fit the vehicle-type routing placeUnassignedJobs uses.
    const unassignedCrossFleetJobs = dataByType[vehicleTypes[0].vehicle_type]?.unassignedCrossFleetJobs || [];
    return afterGeneric.map((s) => placeUnassignedCrossFleetJobs(s, unassignedCrossFleetJobs, dateStr));
  }, [
    baseSheets,
    unassignedJobs,
    compatMatrix,
    dateStr,
    enableUnassignedPlacement,
    isCrossFleetOnly,
    vehicleTypes,
    dataByType,
    confirmedMultiLegGroups,
    multiLegBufferMinutes,
  ]);

  // Overlays published break windows onto each vehicle's own gap slots --
  // see placeScheduledBreaks.
  const finalSheets = useMemo(
    () => placeScheduledBreaks(perTypeSheets, publishedBreaks),
    [perTypeSheets, publishedBreaks]
  );

  const maxLaneCount = Math.max(0, ...finalSheets.map((s) => s.sheet.laneCount));

  const entries: FlatEntry[] = useMemo(() => {
    const rows: { label: string; blocks: SlotBlock[]; isOverflow: boolean; colourFamily: SlotColourFamily }[] = [];
    for (let slotIdx = 0; slotIdx < maxLaneCount; slotIdx++) {
      for (const { type, sheet } of finalSheets) {
        if (slotIdx < sheet.lanes.length) {
          const isOverflow = sheet.hasOverflow && slotIdx === sheet.lanes.length - 1;
          rows.push({
            label: isOverflow ? `${type.vehicle_type} Overflow` : `${type.vehicle_type} ${slotIdx + 1}`,
            blocks: prepareLaneForDisplay(sheet.lanes[slotIdx], type.slot_length_minutes),
            isOverflow,
            colourFamily: getSlotColour(type.vehicle_type, slotIdx + 1, slotColours),
          });
        }
      }
    }

    const flat: FlatEntry[] = [];
    rows.forEach((row) => {
      row.blocks.forEach((block) => {
        const job = block.kind === "job" ? block.job : null;
        flat.push({
          slotLabel: row.label,
          colourFamily: row.colourFamily,
          isOverflow: row.isOverflow,
          block,
          vehicleKey: block.vehicleNumber || BLANK,
          timeKey: formatClockTime(block.startH),
          stageKey: block.kind === "break" ? "Break Time" : job?.Stage || BLANK,
          whenKey: job ? formatIsoTime(job.requested_pickup_at) : BLANK,
          assignKey: job ? formatIsoTime(job.assigned_at) : BLANK,
          pobKey: job ? formatPobTime(job) : BLANK,
          reqKey: job ? requestedTypeBadge(job) || BLANK : BLANK,
          passengerKey: job ? formatPassengerName(job.Passenger_First_Name, job.Passenger_Last_Name) || "Unknown" : BLANK,
          pickupKey: job ? job.Pick_Up_Location || "?" : BLANK,
          destinationKey: job ? job.Drop_Off_Location || "?" : BLANK,
          // Always shown, not just for jobs -- an open gap's duration is how
          // a dispatcher tells a 20-minute opening apart from a multi-hour
          // one when the row's own label just says "Now" or a single start
          // time (see prepareLaneForDisplay's gap-collapsing).
          durationKey: formatMinutes(block.endH - block.startH),
          // A confirmed multi-leg group's own leg numbering (e.g. "3/5")
          // takes priority over the generic tick-split badge -- both use the
          // same slotIndex/slotTotal display convention, but legIndex/
          // legTotal is the more meaningful count for a leg block.
          slotBadge:
            block.kind === "job" && block.legTotal && block.legTotal > 1
              ? `${block.legIndex}/${block.legTotal}`
              : block.slotTotal && block.slotTotal > 1
              ? `${block.slotIndex}/${block.slotTotal}`
              : "1",
        });
      });
    });

    return flat.sort((a, b) => a.block.startH - b.block.startH);
  }, [finalSheets, maxLaneCount, slotColours]);

  const [filters, setFilters] = useState<ColumnFilters>({
    slot: null,
    vehicle: null,
    time: null,
    stage: null,
    when: null,
    assign: null,
    pob: null,
    req: null,
    passenger: null,
    pickup: null,
    destination: null,
    duration: null,
  });

  const distinct = (pick: (e: FlatEntry) => string): string[] =>
    Array.from(new Set(entries.map(pick))).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  const slotValues = useMemo(() => distinct((e) => e.slotLabel), [entries]);
  const vehicleValues = useMemo(() => distinct((e) => e.vehicleKey), [entries]);
  const timeValues = useMemo(() => distinct((e) => e.timeKey), [entries]);
  const stageValues = useMemo(() => distinct((e) => e.stageKey), [entries]);
  const whenValues = useMemo(() => distinct((e) => e.whenKey), [entries]);
  const assignValues = useMemo(() => distinct((e) => e.assignKey), [entries]);
  const pobValues = useMemo(() => distinct((e) => e.pobKey), [entries]);
  const reqValues = useMemo(() => distinct((e) => e.reqKey), [entries]);
  const passengerValues = useMemo(() => distinct((e) => e.passengerKey), [entries]);
  const pickupValues = useMemo(() => distinct((e) => e.pickupKey), [entries]);
  const destinationValues = useMemo(() => distinct((e) => e.destinationKey), [entries]);
  const durationValues = useMemo(() => distinct((e) => e.durationKey), [entries]);

  const filteredEntries = useMemo(
    () =>
      entries.filter(
        (e) =>
          (filters.slot === null || filters.slot.has(e.slotLabel)) &&
          (filters.vehicle === null || filters.vehicle.has(e.vehicleKey)) &&
          (filters.time === null || filters.time.has(e.timeKey)) &&
          (filters.stage === null || filters.stage.has(e.stageKey)) &&
          (filters.when === null || filters.when.has(e.whenKey)) &&
          (filters.assign === null || filters.assign.has(e.assignKey)) &&
          (filters.pob === null || filters.pob.has(e.pobKey)) &&
          (filters.req === null || filters.req.has(e.reqKey)) &&
          (filters.passenger === null || filters.passenger.has(e.passengerKey)) &&
          (filters.pickup === null || filters.pickup.has(e.pickupKey)) &&
          (filters.destination === null || filters.destination.has(e.destinationKey)) &&
          (filters.duration === null || filters.duration.has(e.durationKey))
      ),
    [entries, filters]
  );

  // Which rows are "happening right now" -- any block (gap or job) whose own
  // [startH, endH) window contains this instant, only when viewing today.
  // Rows aren't guaranteed to cluster into a single contiguous band: two
  // vehicles' slots share the same clock instant but not necessarily
  // adjacent tick phases (e.g. one vehicle's shift ends right at "now" while
  // another's doesn't), so a non-matching row can sit between two matching
  // ones. Each such cluster still needs its own clean box rather than one
  // global first/last border, otherwise a single non-matching row splits the
  // highlight into two visually separate flashing boxes -- see isBoxTop/
  // isBoxBottom below, which check per-row neighbours instead of a single
  // global min/max index.
  const nowRowIndices = useMemo(() => {
    if (!isTodayView) return new Set<number>();
    const set = new Set<number>();
    filteredEntries.forEach((e, i) => {
      if (e.block.startH <= nowH && e.block.endH > nowH) set.add(i);
    });
    return set;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredEntries, isTodayView, nowH]);
  // Scroll target for jumpToNow/auto-scroll is always the very first "now"
  // row overall, even when there are multiple disjoint clusters below.
  const firstNowIdx = nowRowIndices.size ? Math.min(...nowRowIndices) : -1;

  if (entries.length === 0) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500 border border-gray-700 rounded-lg bg-gray-900/60">
        <p className="text-lg">No vehicles rostered on this day</p>
      </div>
    );
  }

  return (
    <div className="border border-gray-700 rounded-lg overflow-auto bg-gray-900/60 max-h-[75vh]">
      <table className="border-collapse w-full">
        <thead>
          <tr>
            <th className="sticky top-0 z-10 border border-gray-700 px-1.5 py-1 bg-gray-800" style={{ width: 28 }} />
            <ColumnFilterHeader label="Slot" values={slotValues} selected={filters.slot} onChange={(v) => setFilters((f) => ({ ...f, slot: v }))} width={80} />
            <ColumnFilterHeader label="Vehicle" values={vehicleValues} selected={filters.vehicle} onChange={(v) => setFilters((f) => ({ ...f, vehicle: v }))} width={50} />
            <ColumnFilterHeader label="Time" values={timeValues} selected={filters.time} onChange={(v) => setFilters((f) => ({ ...f, time: v }))} width={70} />
            <ColumnFilterHeader label="Stage" values={stageValues} selected={filters.stage} onChange={(v) => setFilters((f) => ({ ...f, stage: v }))} width={100} />
            <ColumnFilterHeader label="When" values={whenValues} selected={filters.when} onChange={(v) => setFilters((f) => ({ ...f, when: v }))} width={60} title="Requested pickup time" />
            <ColumnFilterHeader label="Assign" values={assignValues} selected={filters.assign} onChange={(v) => setFilters((f) => ({ ...f, assign: v }))} width={60} title="When TaxiCaller assigned a driver" />
            <ColumnFilterHeader label="POB" values={pobValues} selected={filters.pob} onChange={(v) => setFilters((f) => ({ ...f, pob: v }))} width={60} title="Passenger on board time" />
            <ColumnFilterHeader label="Req." values={reqValues} selected={filters.req} onChange={(v) => setFilters((f) => ({ ...f, req: v }))} width={80} title="Vehicle type and passenger/wheelchair/luggage tags the booking itself requested" />
            <ColumnFilterHeader label="Passenger" values={passengerValues} selected={filters.passenger} onChange={(v) => setFilters((f) => ({ ...f, passenger: v }))} width={110} />
            <ColumnFilterHeader label="Pick Up" values={pickupValues} selected={filters.pickup} onChange={(v) => setFilters((f) => ({ ...f, pickup: v }))} />
            <ColumnFilterHeader label="Destination" values={destinationValues} selected={filters.destination} onChange={(v) => setFilters((f) => ({ ...f, destination: v }))} />
            <ColumnFilterHeader label="Duration" values={durationValues} selected={filters.duration} onChange={(v) => setFilters((f) => ({ ...f, duration: v }))} width={60} />
          </tr>
        </thead>
        <tbody>
          {filteredEntries.length === 0 && (
            <tr>
              <td colSpan={13} className="text-center py-6 text-gray-500 text-sm">
                No slots match the current filters.
              </td>
            </tr>
          )}
          {filteredEntries.map((entry, i) => {
            const isJob = entry.block.kind === "job";
            const isBreak = entry.block.kind === "break";
            const isCommitted = entry.block.kind === "committed";
            const job = isJob || isCommitted ? (entry.block as Extract<SlotBlock, { kind: "job" | "committed" }>).job : null;
            // Present when this job block is one leg of a confirmed
            // multi-leg group (see placeUnassignedJobs) -- each leg gets its
            // own row on the same vehicle, numbered legIndex/legTotal.
            const legTotal = isJob ? (entry.block as Extract<SlotBlock, { kind: "job" }>).legTotal : undefined;
            const bg = isBreak ? "#eab308" : isCommitted ? "#4338CA" : entry.isOverflow ? "#111827" : isJob ? entry.colourFamily.light : entry.colourFamily.dark;
            const textColour = isBreak ? "text-black" : isCommitted ? "text-indigo-100" : entry.isOverflow ? "text-red-400" : isJob ? "text-gray-900" : "text-white";
            const isNow = nowRowIndices.has(i);
            // Per-row neighbour check, not a single global min/max index --
            // "now" rows can appear in more than one disjoint cluster (see
            // comment on nowRowIndices above), and each cluster needs its
            // own closed box rather than sharing one top/bottom border.
            const isBoxTop = isNow && !nowRowIndices.has(i - 1);
            const isBoxBottom = isNow && !nowRowIndices.has(i + 1);
            const isFirstNow = i === firstNowIdx;
            const cellCls = `relative border border-gray-700 px-2 py-1 text-[11px] whitespace-nowrap ${textColour}`;
            const nowEdgeCls = isNow
              ? `${isBoxTop ? "fleet-now-border-t" : ""} ${isBoxBottom ? "fleet-now-border-b" : ""}`
              : "";
            const nowOverlay = isNow ? <span className="fleet-now-overlay" aria-hidden="true" /> : null;
            const capacity = entry.block.vehicleNumber ? capacityByVehicle.get(entry.block.vehicleNumber) : undefined;
            return (
              <tr key={i} ref={isFirstNow ? nowRowRef : undefined}>
                <td
                  className={`${cellCls} ${nowEdgeCls} ${isNow ? "fleet-now-border-l" : ""}`}
                  style={{ backgroundColor: bg, width: 28, textAlign: "center" }}>
                  {nowOverlay}
                  {entry.block.isShiftStart && <span title="Shift starts here">⭐</span>}
                  {entry.block.isShiftEnd && <span title="Last slot before shift ends">🚩</span>}
                </td>
                <td className={`${cellCls} ${nowEdgeCls} font-semibold`} style={{ backgroundColor: bg, width: 80 }}>
                  {nowOverlay}
                  {entry.slotLabel}
                  {(job || isBreak) && <span className="font-normal text-gray-400"> ({entry.slotBadge})</span>}
                </td>
                <td
                  className={`${cellCls} ${nowEdgeCls}`}
                  style={{ backgroundColor: bg, width: 50 }}
                  title={vehicleTooltip(capacity) || undefined}
                >
                  {nowOverlay}
                  {entry.block.vehicleNumber || ""}
                </td>
                <td className={`${cellCls} ${nowEdgeCls} font-bold`} style={{ backgroundColor: bg, width: 70 }}>
                  {nowOverlay}
                  {formatSlotTime(entry.block, dateStr)}
                </td>
                <td className={`${cellCls} ${nowEdgeCls}`} style={{ backgroundColor: bg, width: 100 }}>
                  {nowOverlay}
                  {isBreak && (
                    <span className="font-semibold">
                      ☕ Break Time -- {(entry.block as Extract<SlotBlock, { kind: "break" }>).driverName}
                      {(entry.block as Extract<SlotBlock, { kind: "break" }>).taken && (
                        <span title="Confirmed taken"> ✓</span>
                      )}
                    </span>
                  )}
                  {isCommitted && <span className="font-semibold">→ Assigned, heading to pickup</span>}
                  {isJob && job && stageBadge(job.Stage)}
                </td>
                <td className={`${cellCls} ${nowEdgeCls}`} style={{ backgroundColor: bg, width: 60 }}>
                  {nowOverlay}
                  {job && formatIsoTime(job.requested_pickup_at)}
                </td>
                <td className={`${cellCls} ${nowEdgeCls}`} style={{ backgroundColor: bg, width: 60 }}>
                  {nowOverlay}
                  {job && formatIsoTime(job.assigned_at)}
                </td>
                <td className={`${cellCls} ${nowEdgeCls}`} style={{ backgroundColor: bg, width: 60 }}>
                  {nowOverlay}
                  {job && formatPobTime(job)}
                </td>
                <td
                  className={`${cellCls} ${nowEdgeCls}`}
                  style={{ backgroundColor: bg, width: 80, textAlign: "center" }}
                  title={job?.requested_vehicle_type ?? undefined}
                >
                  {nowOverlay}
                  {job && (
                    <div className="flex flex-col items-center gap-0.5">
                      <span className="font-semibold">{requestedTypeBadge(job)}</span>
                      <span className="text-[10px]">{jobCountBadges(job)}</span>
                    </div>
                  )}
                </td>
                <td className={`${cellCls} ${nowEdgeCls}`} style={{ backgroundColor: bg, width: 110, whiteSpace: "normal" }}>
                  {nowOverlay}
                  {job && (formatPassengerName(job.Passenger_First_Name, job.Passenger_Last_Name) || "Unknown")}
                  {legTotal && legTotal > 1 && (
                    <div className="mt-0.5">
                      <span className="inline-block rounded bg-purple-900/70 text-purple-200 text-[9px] px-1 py-0.5 font-semibold" title="Confirmed multi-leg trip -- one vehicle doing several stops in sequence (see Multi-Leg tab)">
                        Multi-leg
                      </span>
                    </div>
                  )}
                </td>
                <td className={`${cellCls} ${nowEdgeCls}`} style={{ backgroundColor: bg, minWidth: 160, whiteSpace: "normal" }}>
                  {nowOverlay}
                  {job && (job.Pick_Up_Location || "?")}
                </td>
                <td className={`${cellCls} ${nowEdgeCls}`} style={{ backgroundColor: bg, minWidth: 160, whiteSpace: "normal" }}>
                  {nowOverlay}
                  {job && (job.Drop_Off_Location || "?")}
                </td>
                <td
                  className={`${cellCls} ${nowEdgeCls} ${isNow ? "fleet-now-border-r" : ""}`}
                  style={{ backgroundColor: bg, width: 60 }}
                >
                  {nowOverlay}
                  {formatMinutes(entry.block.endH - entry.block.startH)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
});

FleetSlotSheet.displayName = "FleetSlotSheet";

export default FleetSlotSheet;
